import { useState } from 'react'
import { PageContainer, ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Alert, App, Button, Space, Tag, Typography, type MenuProps, type TableProps } from 'antd'
import { ChevronDown, ListChecks, Plus } from 'lucide-react'
import { z } from 'zod'
import type { AccountDisplay } from '@super-mega/server/contracts/profile'
import { TableRowActions } from '@/components/table-row-actions.tsx'
import { CREATED_AT_SORT_DIRECTIONS, DEFAULT_PAGE_SEARCH as DEFAULT_QUERY } from '@/const/list.ts'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { AuthorizedDropdown } from '@/features/authorization/authorized-dropdown.tsx'
import { Authorized } from '@/features/authorization/authorized.tsx'
import {
  OPERATION_MODE,
  RoomBatchModal,
  SiteBatchOwnershipModal,
  SiteEditorModal,
  SiteQuickCreateModal
} from '@/features/master-data/index.ts'
import { UserAvatarName } from '@/features/profile/index.ts'
import {
  useMemberCreatorOptions,
  usePageTableNavigation,
  type DateRangeFormValue,
  type PageTableSearch
} from '@/hooks/index.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useRemoteKeyword } from '@/hooks/use-remote-keyword.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { createListQueryPersistence } from '@/utils/list-query-persistence.ts'
import {
  createdAtSearchShape,
  createdAtSortSearchShape,
  creatorMemberSearchShape,
  getCreatedAtSearch,
  getCreatedAtSortOrder,
  getDateRangeFormValue
} from '@/utils/list-query.ts'
import { proRemoteSearch } from '@/utils/pro-select-search.ts'

type OperationMode = 'agent' | 'selfOperated'

type SiteQuery = PageTableSearch & {
  id?: number
  keyword?: string
  creatorMemberId?: number
  createdFrom?: string
  createdTo?: string
  organizationId?: number
  operationMode?: OperationMode
  sortField?: 'createdAt'
  sortOrder?: 'asc' | 'desc'
  status?: 'enabled' | 'disabled'
}

type SiteSearchFormValues = Pick<
  SiteQuery,
  'creatorMemberId' | 'keyword' | 'organizationId' | 'operationMode' | 'status'
> & {
  createdAtRange?: DateRangeFormValue
}

type SiteRow = {
  roomCount: number
  id: number
  name: string
  organizationId: number | null
  organization: { id: number; name: string; isDisable: boolean } | null
  operationMode: OperationMode
  provinceCode: string
  provinceName: string
  cityCode: string
  cityName: string
  districtCode: string | null
  districtName: string | null
  detailAddress: string
  note: string | null
  isDisable: boolean
  creator: AccountDisplay
  createdAt: Date
  updatedAt: Date
}

const siteSearchSchema = z.object({
  current: z.coerce.number().int().min(1).catch(DEFAULT_QUERY.current),
  pageSize: z.coerce.number().int().min(1).max(100).catch(DEFAULT_QUERY.pageSize),
  id: z.coerce.number().int().positive().optional().catch(undefined),
  keyword: z.string().trim().max(255).optional().catch(undefined),
  ...creatorMemberSearchShape,
  ...createdAtSearchShape,
  ...createdAtSortSearchShape,
  organizationId: z.coerce.number().int().positive().optional().catch(undefined),
  operationMode: z.enum(['agent', 'selfOperated']).optional().catch(undefined),
  status: z.enum(['enabled', 'disabled']).optional().catch(undefined)
})

const listQuery = createListQueryPersistence('super-mega:address:sites:list-query', siteSearchSchema)

export const Route = createFileRoute('/_authenticated/address/sites')({
  beforeLoad: (options) => {
    listQuery.restore(options)
  },
  component: SitesPage,
  head: () => ({ meta: [{ title: '场地设置 · Super Mega' }] }),
  validateSearch: listQuery.validateSearch
})

function SitesPage() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { message, modal } = App.useApp()
  const { currentSession } = Route.useRouteContext()
  const navigate = Route.useNavigate()
  const { search: query, navigateSearch } = usePersistedListQuery({
    persistence: listQuery,
    raw: Route.useSearch(),
    accountId: currentSession.id,
    navigate
  })
  const [quickCreating, setQuickCreating] = useState(false)
  const [editing, setEditing] = useState<SiteRow | 'new'>()
  const [ownershipIds, setOwnershipIds] = useState<number[]>()
  const [batchRoomSiteId, setBatchRoomSiteId] = useState<number>()
  const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>([])
  const canManage =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some((permission) => permission.startsWith('site.') && !permission.endsWith('.read'))
  const canReadRooms = currentSession.isSuperAdmin || currentSession.permissions.includes('room.read')
  const canManageRooms =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some((permission) => permission.startsWith('room.') && !permission.endsWith('.read'))
  const sites = useQuery({
    ...trpc.site.list.queryOptions(query),
    placeholderData: keepPreviousData
  })
  const memberCreator = useMemberCreatorOptions('site.read', query.creatorMemberId)
  const organizationSearch = useRemoteKeyword()
  const organizations = useQuery({
    ...trpc.lookup.organizations.queryOptions({ purpose: 'site.read', keyword: organizationSearch.keyword }),
    placeholderData: keepPreviousData
  })
  const organizationOptions = (organizations.data?.data ?? []).map((organization) => ({
    label: organization.isDisable ? `${organization.name}（已停用）` : organization.name,
    value: organization.id
  }))
  const batchDeleteSites = useMutation(trpc.site.batchDelete.mutationOptions())
  const deleteSite = useMutation(trpc.site.delete.mutationOptions())
  const setDisabled = useMutation(trpc.site.setDisabled.mutationOptions())
  const batchSetDisabled = useMutation(trpc.site.batchSetDisabled.mutationOptions())

  const closeEditor = () => setEditing(undefined)
  const invalidateList = () => queryClient.invalidateQueries(trpc.site.list.queryFilter())
  const table = usePageTableNavigation<SiteQuery, SiteSearchFormValues>({
    defaultSearch: DEFAULT_QUERY,
    getFormInitialValues: (search) => ({
      createdAtRange: getDateRangeFormValue(search.createdFrom, search.createdTo),
      creatorMemberId: search.creatorMemberId,
      keyword: search.keyword ?? (search.id === undefined ? undefined : String(search.id)),
      organizationId: search.organizationId,
      operationMode: search.operationMode,
      status: search.status
    }),
    getSearchFromForm: (values) => ({
      ...getCreatedAtSearch(values.createdAtRange),
      creatorMemberId: values.creatorMemberId,
      id: undefined,
      keyword: values.keyword?.trim() || undefined,
      organizationId: values.organizationId,
      operationMode: values.operationMode,
      status: values.status
    }),
    navigate: navigateSearch,
    onQueryChange: () => {
      closeEditor()
      setSelectedSiteIds([])
    },
    search: query,
    showTotal: (total) => `共 ${total} 个场地`
  })

  const handleTableChange: NonNullable<TableProps<SiteRow>['onChange']> =
    /* eslint-disable max-params -- Ant Design Table 的回调签名由组件定义。 */
    (_pagination, _filters, sorter, extra) => {
      if (extra.action !== 'sort') {
        return
      }
      const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter
      const sortOrder = getCreatedAtSortOrder(currentSorter.order)
      closeEditor()
      setSelectedSiteIds([])
      void navigate({
        replace: true,
        search: (previous) => ({
          ...previous,
          current: 1,
          sortField: 'createdAt',
          sortOrder
        })
      })
    }
  /* eslint-enable max-params */

  const changeStatus = async (site: SiteRow) => {
    try {
      await setDisabled.mutateAsync({ id: site.id, isDisabled: !site.isDisable })
      void message.success(site.isDisable ? '场地已恢复' : '场地已停用')
      await invalidateList()
    } catch (error) {
      void message.error(getErrorMessage(error, '场地状态更新失败'))
    }
  }

  const changeSelectedStatus = async (isDisabled: boolean) => {
    try {
      const result = await batchSetDisabled.mutateAsync({ ids: selectedSiteIds, isDisabled })
      setSelectedSiteIds([])
      void message.success(`已${isDisabled ? '停用' : '恢复'} ${result.count} 个场地`)
      await invalidateList()
    } catch (error) {
      void message.error(getErrorMessage(error, '批量更新场地状态失败'))
    }
  }

  const confirmDeleteSites = (ids: number[], siteName?: string) => {
    const targetIds = [...ids]
    const targetIdSet = new Set(targetIds)
    modal.confirm({
      title: siteName
        ? `永久删除场地 ${siteName}（ID：${targetIds[0]}）？`
        : `永久删除已选 ${targetIds.length} 个场地？`,
      content:
        '删除后无法恢复。未删除房源（含停用房源）、租赁订单或占用记录会阻止删除；已删除且无业务关联的房源记录会一并清理。任一场地不符合条件，整批都不会删除。',
      focusable: { autoFocusButton: 'cancel' },
      okText: siteName ? '永久删除' : '确认批量删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          if (siteName) {
            await deleteSite.mutateAsync(targetIds[0])
          } else {
            await batchDeleteSites.mutateAsync({ ids: targetIds })
          }
        } catch (error) {
          void message.error(getErrorMessage(error, '删除场地失败'))
          throw error
        }
        closeEditor()
        setSelectedSiteIds([])
        void message.success(`已删除 ${targetIds.length} 个场地`)
        await Promise.all([invalidateList(), queryClient.invalidateQueries(trpc.site.options.queryFilter())])
        if (sites.data && sites.data.data.every((site) => targetIdSet.has(site.id)) && query.current > 1) {
          await navigateSearch({
            replace: true,
            search: (previous) => ({ ...previous, current: previous.current - 1 })
          })
        }
      }
    })
  }

  const confirmChangeStatus = (site: SiteRow) => {
    modal.confirm({
      content: site.isDisable ? '恢复后可继续用于业务。' : '停用后历史数据仍会保留。',
      okButtonProps: site.isDisable ? undefined : { danger: true },
      onOk: () => changeStatus(site),
      title: site.isDisable ? '确认恢复场地？' : '确认停用场地？'
    })
  }

  const confirmChangeSelectedStatus = (isDisabled: boolean) => {
    if (!isDisabled) {
      void changeSelectedStatus(false)
      return
    }
    modal.confirm({
      content: '停用后，选中的场地及其后续业务入口将不可继续使用。',
      okButtonProps: { danger: true },
      okText: '确认停用',
      onOk: () => changeSelectedStatus(true),
      title: `停用已选 ${selectedSiteIds.length} 个场地？`
    })
  }

  const batchMenu: MenuProps = {
    items: [
      { key: 'ownership', label: '修改经营类型与归属主体' },
      { key: 'enable', label: '批量恢复' },
      { danger: true, key: 'disable', label: '批量停用' },
      { type: 'divider' },
      { danger: true, key: 'delete', label: '批量删除' }
    ],
    onClick: ({ key }) =>
      key === 'ownership'
        ? setOwnershipIds([...selectedSiteIds])
        : key === 'delete'
          ? confirmDeleteSites(selectedSiteIds)
          : confirmChangeSelectedStatus(key === 'disable')
  }

  const columns: ProColumns<SiteRow>[] = [
    {
      dataIndex: 'keyword',
      fieldProps: { allowClear: true, placeholder: '场地名称、地址或 ID' },
      hideInTable: true,
      title: '关键词 / ID'
    },
    {
      dataIndex: 'creatorMemberId',
      fieldProps: {
        allowClear: true,
        loading: memberCreator.isLoading,
        options: memberCreator.options,
        placeholder: '选择创建人',
        ...proRemoteSearch(memberCreator.onSearch)
      },
      hideInTable: true,
      title: '创建人',
      valueType: 'select'
    },
    {
      dataIndex: 'createdAtRange',
      hideInTable: true,
      title: '创建时间',
      fieldProps: { presets: RANGE_PRESETS },
      valueType: 'dateRange'
    },
    {
      dataIndex: 'id',
      copyable: true,
      ellipsis: true,
      renderText: (value?: number) => (value === undefined ? '—' : String(value)),
      search: false,
      title: 'ID',
      width: '7rem'
    },
    {
      dataIndex: 'name',
      ellipsis: true,
      render: (_, site) => (
        <Space className="w-full min-w-0" orientation="vertical" size={0}>
          <Typography.Text className="block max-w-full truncate" strong>
            {site.name}
          </Typography.Text>
          <Typography.Text className="block max-w-full truncate" type="secondary">
            {[site.provinceName, site.cityName, site.districtName]
              .filter((name, index, names) => name && (index === 0 || name !== names[index - 1]))
              .join(' / ')}
          </Typography.Text>
        </Space>
      ),
      search: false,
      title: '场地',
      width: '16rem'
    },
    {
      dataIndex: 'organizationId',
      fieldProps: {
        allowClear: true,
        loading: organizations.isFetching,
        options: organizationOptions,
        placeholder: '输入名称搜索主体',
        ...proRemoteSearch(organizationSearch.onSearch)
      },
      hideInTable: true,
      title: '归属主体',
      valueType: 'select'
    },
    {
      dataIndex: 'roomCount',
      title: '总房间数',
      tooltip: '包含已停用房间，不包含已删除房间',
      search: false,
      align: 'right',
      width: '8rem'
    },
    {
      dataIndex: 'operationMode',
      title: '经营类型',
      valueEnum: {
        agent: { text: '代理' },
        selfOperated: { text: '自营' }
      },
      render: (_, site) => {
        const mode = OPERATION_MODE[site.operationMode]
        return <Tag color={mode.color}>{mode.text}</Tag>
      },
      width: '7rem'
    },
    {
      dataIndex: 'organization',
      render: (_, site) =>
        site.organization === null ? (
          <Typography.Text type="secondary">—</Typography.Text>
        ) : site.organization.isDisable ? (
          <Tag>{site.organization.name}（已停用）</Tag>
        ) : (
          <Typography.Text>{site.organization.name}</Typography.Text>
        ),
      search: false,
      title: '归属主体',
      width: '13rem'
    },
    {
      dataIndex: 'detailAddress',
      ellipsis: true,
      render: (_, site) =>
        `${[site.provinceName, site.cityName, site.districtName]
          .filter((name, index, names) => name && (index === 0 || name !== names[index - 1]))
          .join('')}${site.detailAddress}`,
      search: false,
      title: '详细地址',
      width: '20rem'
    },
    {
      dataIndex: 'status',
      hideInTable: true,
      title: '状态',
      valueEnum: {
        enabled: { text: '正常' },
        disabled: { text: '已停用' }
      }
    },
    {
      dataIndex: 'isDisable',
      render: (_, site) => (
        <Tag color={site.isDisable ? 'default' : 'success'}>{site.isDisable ? '已停用' : '正常'}</Tag>
      ),
      search: false,
      title: '状态',
      width: '6.5rem'
    },
    {
      dataIndex: 'creator',
      render: (_, site) => <UserAvatarName avatar={site.creator.avatar} name={site.creator.name} size={28} />,
      search: false,
      title: '创建人',
      width: '10rem'
    },
    {
      dataIndex: 'createdAt',
      search: false,
      sortOrder: query.sortOrder === 'asc' ? 'ascend' : 'descend',
      sorter: true,
      sortDirections: CREATED_AT_SORT_DIRECTIONS,
      title: '创建时间',
      valueType: 'dateTime',
      width: '12rem'
    },
    {
      dataIndex: 'updatedAt',
      search: false,
      title: '最近更新',
      valueType: 'dateTime',
      width: '12rem'
    },
    {
      align: 'center',
      fixed: 'right',
      render: (_, site) => {
        if (!canReadRooms && !canManage && !canManageRooms) {
          return '—'
        }
        return (
          <TableRowActions
            resource="site"
            actions={[
              ...(canReadRooms
                ? [
                    {
                      key: 'rooms',
                      label: '查看房间',
                      onClick: () =>
                        void navigate({
                          search: { current: 1, pageSize: 20, siteId: site.id },
                          to: '/address/rooms'
                        })
                    }
                  ]
                : []),
              ...(canManageRooms
                ? [
                    {
                      disabled: site.isDisable,
                      key: 'batch-create-rooms',
                      label: '批量生成房间',
                      onClick: () => setBatchRoomSiteId(site.id)
                    }
                  ]
                : []),
              ...(canManage
                ? [
                    { key: 'edit', label: '编辑', onClick: () => setEditing(site) },
                    {
                      danger: !site.isDisable,
                      key: 'status',
                      label: site.isDisable ? '恢复' : '停用',
                      onClick: () => confirmChangeStatus(site)
                    },
                    {
                      key: 'delete',
                      label: '删除场地',
                      danger: true,
                      onClick: () => confirmDeleteSites([site.id], site.name)
                    }
                  ]
                : [])
            ]}
          />
        )
      },
      search: false,
      title: '操作',
      valueType: 'option',
      width: '11rem'
    }
  ]

  return (
    <PageContainer breadcrumbRender={false} content="记录当前管理的物业、经营模式与标准地址。" title="场地设置">
      {sites.isError ? (
        <Alert
          action={
            <Button onClick={() => void sites.refetch()} size="small">
              重试
            </Button>
          }
          className="mb-4"
          showIcon
          title={getErrorMessage(sites.error, '场地列表加载失败')}
          type="error"
        />
      ) : null}
      <ProTable<SiteRow, SiteSearchFormValues>
        columns={columns}
        columnsState={{ persistenceKey: 'master-data-sites:table', persistenceType: 'localStorage' }}
        dataSource={sites.data?.data ?? []}
        loading={{ delay: 150, spinning: sites.isFetching }}
        onChange={handleTableChange}
        onReset={table.onReset}
        onSubmit={table.onSubmit}
        options={{ reload: () => void sites.refetch() }}
        pagination={{ ...table.pagination, total: sites.data?.total ?? 0 }}
        formRef={table.formRef}
        rowKey="id"
        rowSelection={
          canManage
            ? {
                onChange: (keys) => setSelectedSiteIds(keys.map(Number)),
                selectedRowKeys: selectedSiteIds,
                preserveSelectedRowKeys: false,
                getCheckboxProps: () => ({
                  disabled:
                    sites.isFetching || batchDeleteSites.isPending || deleteSite.isPending || batchSetDisabled.isPending
                })
              }
            : undefined
        }
        scroll={{ x: '124rem' }}
        form={{ initialValues: table.formInitialValues }}
        search={{ defaultCollapsed: false, labelWidth: 'auto' }}
        size="small"
        toolBarRender={() =>
          canManage
            ? [
                <AuthorizedDropdown
                  resource="site"
                  key="batch-actions"
                  menu={batchMenu}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Button
                    disabled={
                      selectedSiteIds.length === 0 ||
                      sites.isFetching ||
                      deleteSite.isPending ||
                      batchSetDisabled.isPending
                    }
                    loading={batchDeleteSites.isPending}
                    icon={<ListChecks aria-hidden size={16} />}
                  >
                    {selectedSiteIds.length ? `批量操作（${selectedSiteIds.length}）` : '批量操作'}{' '}
                    <ChevronDown aria-hidden size={14} />
                  </Button>
                </AuthorizedDropdown>,
                ...(canManageRooms
                  ? [
                      <Authorized key="quick-create" permissions={['site.create']}>
                        <Button
                          key="quick-create"
                          icon={<Plus aria-hidden size={16} />}
                          onClick={() => setQuickCreating(true)}
                        >
                          快速创建
                        </Button>
                      </Authorized>
                    ]
                  : []),
                <Authorized key="new" permissions={['site.create']}>
                  <Button
                    icon={<Plus aria-hidden size={16} />}
                    key="new"
                    onClick={() => {
                      setEditing('new')
                    }}
                    type="primary"
                  >
                    新建场地
                  </Button>
                </Authorized>
              ]
            : []
        }
      />

      {quickCreating && (
        <SiteQuickCreateModal onClose={() => setQuickCreating(false)} onCreated={() => setQuickCreating(false)} />
      )}
      {ownershipIds ? (
        <SiteBatchOwnershipModal
          ids={ownershipIds}
          onClose={() => setOwnershipIds(undefined)}
          onSaved={() => {
            setOwnershipIds(undefined)
            setSelectedSiteIds([])
          }}
        />
      ) : null}
      <RoomBatchModal
        defaultSiteId={batchRoomSiteId}
        onClose={() => setBatchRoomSiteId(undefined)}
        onSaved={async () => {
          setBatchRoomSiteId(undefined)
          await Promise.all([
            invalidateList(),
            queryClient.invalidateQueries(trpc.room.list.queryFilter()),
            queryClient.invalidateQueries(trpc.room.siteOptions.queryFilter())
          ])
        }}
        open={batchRoomSiteId !== undefined}
        siteLocked
        siteOptions={
          sites.data?.data
            .filter((site) => site.id === batchRoomSiteId)
            .map((site) => ({
              label: site.isDisable ? `${site.name}（已停用）` : site.name,
              value: site.id
            })) ?? []
        }
      />
      <SiteEditorModal
        editing={editing}
        onClose={closeEditor}
        onSaved={async () => {
          closeEditor()
          await invalidateList()
        }}
      />
    </PageContainer>
  )
}
